// src/components/captures/CaptureToTaskModal.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Capture } from '../../types/capture';
import { TaskCategory, TaskPriority, TaskEnergyLevel } from '../../types/task';
import { analyzeCaptureForTasks } from '../../services/api/captures';
import { taskService } from '../../features/task';
import { X, AlertCircle, Loader, Box, Sparkles, Plus, WifiOff, RefreshCw } from '../common/icons';
import { useToast } from '../../contexts/ToastContext';
import TaskCandidateItem from './TaskCandidateItem';
import { logInfo, logWarn, logError } from '../../utils/logger';

// 常數定義
const MAX_TITLE_LENGTH = 30;

/**
 * 從 Capture 內容中提取標題和備註
 * Local-first 設計：無需網路即可快速轉換
 */
function extractTitleAndNotes(content: string): { title: string; notes: string | null } {
  const trimmed = content.trim();

  // 情況 1: 內容 ≤ 30 字 → 全部當標題
  if (trimmed.length <= MAX_TITLE_LENGTH) {
    return { title: trimmed, notes: null };
  }

  // 情況 2: 有換行 → 第一行當標題（如果不太長）
  const firstLineBreak = trimmed.indexOf('\n');
  if (firstLineBreak > 0 && firstLineBreak <= MAX_TITLE_LENGTH) {
    return {
      title: trimmed.substring(0, firstLineBreak).trim(),
      notes: trimmed // 全文放入 notes
    };
  }

  // 情況 3: 長文本無換行 → 智慧截斷
  // 在標點符號或空格處截斷，避免斷詞
  let cutPoint = MAX_TITLE_LENGTH;
  const punctuation = /[，。、！？；：,.\s]/;

  for (let i = MAX_TITLE_LENGTH - 1; i >= MAX_TITLE_LENGTH - 10; i--) {
    if (punctuation.test(trimmed[i])) {
      cutPoint = i;
      break;
    }
  }

  return {
    title: trimmed.substring(0, cutPoint).trim(),
    notes: trimmed // 全文放入 notes
  };
}

interface TaskCandidate {
  id: string; // 前端生成的唯一標識
  title: string;
  notes?: string;
  category?: TaskCategory;
  priority?: TaskPriority;
  energyLevel?: TaskEnergyLevel;
  estimatedTime?: number;
  confidence: number;
  targetDate?: string;
  isSelected: boolean; // 用戶選擇狀態
}

interface CaptureToTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  capture: Capture;
  onTasksCreated: () => void; // 任務建立完成後的回調
  showLessonTip?: boolean; // Lesson 模式下在 Sparkles 按鈕旁顯示引導 Tip
}

const CaptureToTaskModal: React.FC<CaptureToTaskModalProps> = ({
  isOpen,
  onClose,
  capture,
  onTasksCreated,
  showLessonTip = false,
}) => {
  // 狀態管理
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<{
    shouldConvert: boolean;
    reasoning: string;
    userMessage: string;
    overallConfidence: number;
  } | null>(null);
  const [taskCandidates, setTaskCandidates] = useState<TaskCandidate[]>([]);
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const [creationProgress, setCreationProgress] = useState<{
    current: number;
    total: number;
    currentTask?: string;
  } | null>(null);

  // AI 解析狀態
  const [hasAIAnalyzed, setHasAIAnalyzed] = useState(false);

  // Lesson Tip：Modal 開啟後 800ms 顯示，用戶點 AI 分析後消失
  const [showSparklesTip, setShowSparklesTip] = useState(false);

  // 離線狀態檢測
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // 新建任務 ID（用於自動展開）
  const [newTaskId, setNewTaskId] = useState<string | null>(null);

  // 排序狀態 - 已簡化: 固定使用 AI 信心排序

  // Toast notifications
  const { showError, showWarning } = useToast();

  // 監聽網路狀態變化
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 重置狀態
  const resetState = useCallback(() => {
    setIsAnalyzing(false);
    setAnalysisError(null);
    setAnalysisResult(null);
    setTaskCandidates([]);
    setIsCreatingTasks(false);
    setCreationProgress(null);
    setHasAIAnalyzed(false);
    setNewTaskId(null);
    setShowSparklesTip(false);
  }, []);


  // ESC 鍵支持
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // 關閉 Modal
        if (!isAnalyzing && !isCreatingTasks) {
          resetState();
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isAnalyzing, isCreatingTasks, resetState, onClose]);

  // Modal 開啟時初始化 - Local-first：立即產生本地 Task
  useEffect(() => {
    if (isOpen && capture) {
      resetState();

      // Local-first: 直接用本地規則產生一個 Task（無需等待 AI）
      const { title, notes } = extractTitleAndNotes(capture.content);
      const initialTask: TaskCandidate = {
        id: 'task-0',
        title,
        notes: notes || undefined,
        confidence: 1, // 本地產生的任務，信心度為 1
        isSelected: true
      };
      setTaskCandidates([initialTask]);

      // Lesson 模式：延遲 800ms 顯示 Sparkles 引導 Tip
      if (showLessonTip) {
        const timer = setTimeout(() => setShowSparklesTip(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, capture, resetState, showLessonTip]);

  // 任務排序邏輯 - 固定使用 AI 信心排序 (Best Match)
  const sortedTasks = useMemo(() => {
    if (taskCandidates.length === 0) return [];
    return [...taskCandidates].sort((a, b) => b.confidence - a.confidence);
  }, [taskCandidates]);

  // 開始 AI 分析（手動觸發）
  const startAnalysis = async () => {
    if (!isOnline) {
      showWarning('Offline', 'Analysis requires an internet connection');
      return;
    }

    // 用戶點了 AI 分析，隱藏引導 Tip
    setShowSparklesTip(false);
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await analyzeCaptureForTasks(
        capture.id,
        capture.content,
        capture.userId
      );

      setAnalysisResult({
        shouldConvert: result.shouldConvert,
        reasoning: result.reasoning,
        userMessage: result.userMessage,
        overallConfidence: result.overallConfidence
      });

      setHasAIAnalyzed(true);

      if (result.shouldConvert && result.extractedTasks.length > 0) {
        // 按信心分數排序（高到低），然後轉換為 TaskCandidate 格式並預設全選
        const sortedTasks = result.extractedTasks.sort((a, b) => b.confidence - a.confidence);

        const candidates: TaskCandidate[] = sortedTasks.map((task, index) => ({
          id: `task-${index}`,
          title: task.title,
          notes: task.notes,
          category: task.category,
          priority: task.priority,
          energyLevel: task.energyLevel,
          estimatedTime: task.estimatedTime,
          confidence: task.confidence,
          targetDate: task.targetDate,
          isSelected: true // 預設選中
        }));

        setTaskCandidates(candidates);
      } else {
        // AI 認為不需要轉換，但保留現有任務讓用戶決定
        showWarning('Suggestion', result.userMessage || 'This may not need to be a task');
      }
    } catch (error) {
      logError('AI 分析失敗', error);
      setAnalysisError(error instanceof Error ? error.message : 'AI 分析時發生未知錯誤');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 手動新增任務
  const addNewTask = () => {
    const newId = `task-${Date.now()}`;
    const newTask: TaskCandidate = {
      id: newId,
      title: '',
      confidence: 1,
      isSelected: true
    };
    setNewTaskId(newId); // 標記為新建，讓它自動展開
    setTaskCandidates(prev => [...prev, newTask]);
  };

  // 刪除任務
  const deleteTask = (taskId: string) => {
    setTaskCandidates(prev => prev.filter(task => task.id !== taskId));
  };

  // 切換任務選擇狀態
  const toggleTaskSelection = (taskId: string) => {
    setTaskCandidates(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, isSelected: !task.isSelected } : task
      )
    );
  };


  // 選擇統計
  const selectedCount = taskCandidates.filter(task => task.isSelected).length;
  const totalCount = taskCandidates.length;


  // 保存編輯
  const saveEdit = (taskId: string, updates: Partial<TaskCandidate>) => {
    setTaskCandidates(prev => 
      prev.map(task => 
        task.id === taskId 
          ? { ...task, ...updates }
          : task
      )
    );
  };

  // 建立選中的任務
  const createSelectedTasks = async () => {
    const selectedTasks = taskCandidates.filter(task => task.isSelected);
    
    if (selectedTasks.length === 0) {
      showWarning('No tasks selected', 'Please select at least one task to convert');
      return;
    }

    setIsCreatingTasks(true);
    setCreationProgress({ current: 0, total: selectedTasks.length });

    const createdTasks = [];
    const failedTasks = [];

    try {
      for (let i = 0; i < selectedTasks.length; i++) {
        const task = selectedTasks[i];
        setCreationProgress({ 
          current: i, 
          total: selectedTasks.length,
          currentTask: task.title 
        });

        try {
          const createdTask = await taskService.createTask({
            title: task.title,
            notes: task.notes,
            category: task.category,
            priority: task.priority,
            energyLevel: task.energyLevel,
            estimatedTime: task.estimatedTime,
            userId: capture.userId,
            originCaptureId: capture.id
          });

          createdTasks.push(createdTask);
          
          // 短暫延遲避免過於頻繁的請求
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          logError('建立任務失敗', { title: task.title, error });
          failedTasks.push({ task, error: error instanceof Error ? error.message : '未知錯誤' });
        }
      }

      setCreationProgress({ current: selectedTasks.length, total: selectedTasks.length });

      // 顯示建立結果（成功不顯示 toast，結果已反映在 UI；只通知異常狀況）
      if (createdTasks.length > 0 && failedTasks.length === 0) {
        logInfo('成功建立任務', { count: createdTasks.length });
      } else if (failedTasks.length > 0) {
        logWarn('部分任務建立失敗', { failedCount: failedTasks.length, failedTasks });
        showWarning(
          'Partial Success',
          `Created ${createdTasks.length} tasks, ${failedTasks.length} failed`
        );
      }

      // 通知上層組件任務已建立
      // 父組件 (CaptureCard) 會負責關閉 modal 和後續導航
      onTasksCreated();

    } catch (error) {
      logError('批量建立任務過程中出現錯誤', error);
      showError('Task Creation Failed', 'There was an issue creating tasks. Please check the results.');
    } finally {
      setIsCreatingTasks(false);
      setCreationProgress(null);
    }
  };

  // 關閉 Modal
  const handleClose = () => {
    // 創建任務時不允許關閉，避免數據不一致
    if (!isCreatingTasks) {
      resetState();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center sm:p-4 z-modal">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-xl sm:max-w-2xl sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header - 簡化設計 */}
        <div className="header-wrapper">
          <div className="header-container">
            <div className="header-title-area">
              <div className="header-title">Convert to Tasks</div>
            </div>
            <div className="header-actions">
              <button
                onClick={handleClose}
                disabled={isCreatingTasks}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                aria-label="Close"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto flex flex-col">

          {/* 離線狀態提示 */}
          {!isOnline && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
              <WifiOff size={18} className="text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Offline mode — analysis unavailable, but you can still create tasks
              </p>
            </div>
          )}

          {/* 原始內容顯示 */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Original Capture
            </label>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                {capture.content}
              </p>
            </div>
          </div>


          {/* 分析錯誤 */}
          {analysisError && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-red-500 mt-0.5 flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <p className="text-red-700 text-sm">{analysisError}</p>
                    <button
                      onClick={startAnalysis}
                      className="mt-2 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded-md text-xs font-medium transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI 分析結果摘要 */}
          {analysisResult && hasAIAnalyzed && (
            <div className="mb-4">
              <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-r from-gray-50 to-gray-50 border border-gray-200">
                {/* 第一行：圖示 + Insight 標題 */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      {analysisResult.shouldConvert ? (
                        <Box size={20} className="text-sky-500" strokeWidth={2} />
                      ) : (
                        <AlertCircle className="text-yellow-500" size={20} strokeWidth={2} />
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-600">
                      Insight
                    </div>
                  </div>
                  <button
                    onClick={startAnalysis}
                    disabled={!isOnline || isAnalyzing}
                    className="flex items-center justify-center p-1.5 text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-sky-600"
                    title="Re-analyze"
                    aria-label="Re-analyze"
                  >
                    <RefreshCw size={16} strokeWidth={2} />
                  </button>
                </div>
                {/* AI 訊息說明 */}
                <p className="text-sm text-gray-700 leading-relaxed">
                  {analysisResult.userMessage}
                </p>
              </div>
            </div>
          )}

          {/* 任務列表區 */}
          <div className="mb-4">
            {/* 任務控制列 - Apple 風格極簡工具列 */}
            <div className="flex items-center justify-between mb-3">
              {/* 左側: 標題和選擇計數 */}
              <div className="flex items-center gap-3">
                <span className="text-base font-medium text-gray-700">
                  Tasks
                </span>
                {taskCandidates.length > 1 && (
                  <span className="text-sm text-gray-500">
                    {selectedCount} of {totalCount} selected
                  </span>
                )}
              </div>

              {/* 右側: 操作按鈕組 */}
              <div className="flex items-center gap-2">
                {/* AI Analyze 按鈕 - 純 Icon (Apple 風格) */}
                {!hasAIAnalyzed && !isAnalyzing && (
                  <div className="relative">
                    <button
                      onClick={startAnalysis}
                      disabled={!isOnline}
                      className="flex items-center justify-center p-2.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-sky-600"
                      title="Analyze"
                      aria-label="Analyze capture"
                    >
                      <Sparkles size={18} strokeWidth={2} />
                    </button>
                    {/* Lesson 引導 Tip */}
                    {showSparklesTip && (
                      <div className="absolute bottom-full right-0 mb-2 w-52 bg-sky-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-tooltip">
                        <p className="leading-snug">Turn vague ideas into clear tasks</p>
                        <div className="absolute top-full right-3 w-2 h-2 bg-sky-900 rotate-45 -mt-1" />
                      </div>
                    )}
                  </div>
                )}

                {/* AI Analyzing 狀態 - 緊湊顯示 */}
                {isAnalyzing && (
                  <div className="flex items-center justify-center p-2.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] text-sky-600">
                    <Loader className="animate-spin" size={18} strokeWidth={2} />
                  </div>
                )}

                {/* Add Task 按鈕 - 純 Icon (Apple 風格) */}
                <button
                  onClick={addNewTask}
                  className="flex items-center justify-center p-2.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-all"
                  title="Add new task"
                  aria-label="Add new task"
                >
                  <Plus size={18} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* 任務候選列表 */}
            {taskCandidates.length > 0 ? (
              <div className="space-y-3 sm:space-y-2">
                {sortedTasks.map((task) => (
                  <TaskCandidateItem
                    key={task.id}
                    task={task}
                    onToggleSelection={() => toggleTaskSelection(task.id)}
                    onSaveEdit={(updates) => {
                      saveEdit(task.id, updates);
                      // 清除新建標記（用戶開始編輯後）
                      if (newTaskId === task.id) {
                        setNewTaskId(null);
                      }
                    }}
                    onDelete={() => deleteTask(task.id)}
                    showDeleteButton={taskCandidates.length > 1}
                    autoExpand={task.id === newTaskId}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No tasks yet</p>
                <button
                  onClick={addNewTask}
                  className="mt-2 text-sky-600 hover:text-sky-800 text-sm font-medium"
                >
                  Add your first task
                </button>
              </div>
            )}
          </div>

          {/* 任務建立進度 */}
          {isCreatingTasks && creationProgress && (
            <div className="mb-6">
              <div className="bg-sky-50 border border-sky-200 rounded-md p-4">
                <div className="flex items-center mb-2">
                  <Loader className="animate-spin text-sky-500 mr-3" size={20} />
                  <span className="text-sky-800 font-medium">Creating Tasks...</span>
                </div>

                <div className="mb-2">
                  <div className="bg-sky-200 rounded-full h-2">
                    <div
                      className="bg-sky-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(creationProgress.current / creationProgress.total) * 100}%` 
                      }}
                    />
                  </div>
                </div>
                
                <div className="text-sm text-sky-700">
                  {creationProgress.current} of {creationProgress.total} tasks
                  {creationProgress.currentTask && (
                    <span className="ml-2">- Creating: "{creationProgress.currentTask}"</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {taskCandidates.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-25 px-4 sm:px-6 py-3 sm:py-4 pb-safe sm:rounded-b-lg">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={isCreatingTasks}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createSelectedTasks}
                disabled={isCreatingTasks || selectedCount === 0}
                className="px-6 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isCreatingTasks
                  ? 'Creating...'
                  : selectedCount === 1
                    ? 'Create Task'
                    : `Create ${selectedCount} Tasks`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default CaptureToTaskModal;